import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { vonDataService } from '@/services/vonDataService';
import { Von04RespCourse } from '@/types/vonData.types';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save } from 'lucide-react';

interface Von04RespCourseFormProps {
  patientId: number;
  onSaved?: () => void;
  readOnly?: boolean;
}

export const Von04RespCourseForm: React.FC<Von04RespCourseFormProps> = ({
  patientId,
  onSaved,
  readOnly = false
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);

  const [ox36, setOx36] = useState<string>('');
  const [vent36, setVent36] = useState<string>('');
  const [hfv36, setHfv36] = useState<string>('');
  const [nazalCanule, setNazalCanule] = useState(false);
  const [ncf36, setNcf36] = useState<string>('');
  const [nimv36, setNimv36] = useState<string>('');
  const [cpap36, setCpap36] = useState<string>('');
  const [steroidsCld, setSteroidsCld] = useState(false);
  const [steroidsCldWhere, setSteroidsCldWhere] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await vonDataService.getRespCourse(patientId);

      if (data) {
        setHasExistingData(true);
        setOx36(data.ox36?.toString() || '');
        setVent36(data.vent36?.toString() || '');
        setHfv36(data.hfv36?.toString() || '');
        setNazalCanule(Boolean(data.nazal_canule));
        setNcf36(data.ncf36?.toString() || '');
        setNimv36(data.nimv36?.toString() || '');
        setCpap36(data.cpap36?.toString() || '');
        setSteroidsCld(Boolean(data.steroids_cld));
        setSteroidsCldWhere(data.steroids_cld_where?.toString() || '');
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;

    try {
      setSubmitting(true);

      const formData: Partial<Von04RespCourse> = {
        patient_id: patientId,
        ox36: ox36 ? parseInt(ox36) : undefined,
        vent36: vent36 ? parseInt(vent36) : undefined,
        hfv36: hfv36 ? parseInt(hfv36) : undefined,
        nazal_canule: nazalCanule,
        ncf36: ncf36 ? parseInt(ncf36) : undefined,
        nimv36: nimv36 ? parseInt(nimv36) : undefined,
        cpap36: cpap36 ? parseInt(cpap36) : undefined,
        steroids_cld: steroidsCld,
        steroids_cld_where: steroidsCldWhere ? parseInt(steroidsCldWhere) : undefined
      };

      if (hasExistingData) {
        await vonDataService.updateRespCourse(patientId, formData);
      } else {
        await vonDataService.createRespCourse(patientId, formData);
        setHasExistingData(true);
      }

      toast({
        title: 'Succes',
        description: 'Datele au fost salvate cu succes'
      });

      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut salva datele',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Secțiunea 04: Suport la 36 săptămâni</CardTitle>
        <CardDescription>Suport respirator la 36 săptămâni vârstă gestațională</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Support at 36 weeks */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ox36">Oxigen la 36 săptămâni</Label>
                <Select value={ox36} onValueChange={setOx36} disabled={readOnly}>
                  <SelectTrigger id="ox36">
                    <SelectValue placeholder="Selectează..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Nu</SelectItem>
                    <SelectItem value="1">Da</SelectItem>
                    <SelectItem value="9">N/A</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="vent36">Ventilație convențională la 36 săptămâni</Label>
                <Select value={vent36} onValueChange={setVent36} disabled={readOnly}>
                  <SelectTrigger id="vent36">
                    <SelectValue placeholder="Selectează..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Nu</SelectItem>
                    <SelectItem value="1">Da</SelectItem>
                    <SelectItem value="9">N/A</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="hfv36">HFV la 36 săptămâni</Label>
                <Select value={hfv36} onValueChange={setHfv36} disabled={readOnly}>
                  <SelectTrigger id="hfv36">
                    <SelectValue placeholder="Selectează..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Nu</SelectItem>
                    <SelectItem value="1">Da</SelectItem>
                    <SelectItem value="9">N/A</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="nazalCanule"
                  checked={nazalCanule}
                  onCheckedChange={(checked) => setNazalCanule(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="nazalCanule" className="text-sm font-normal cursor-pointer">
                  Canulă nazală
                </Label>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ncf36">Flux nazal {'>'} 2 L/min la 36 săptămâni</Label>
                <Select value={ncf36} onValueChange={setNcf36} disabled={readOnly}>
                  <SelectTrigger id="ncf36">
                    <SelectValue placeholder="Selectează..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Nu</SelectItem>
                    <SelectItem value="1">Da</SelectItem>
                    <SelectItem value="9">N/A</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="nimv36">Ventilație nazală la 36 săptămâni</Label>
                <Select value={nimv36} onValueChange={setNimv36} disabled={readOnly}>
                  <SelectTrigger id="nimv36">
                    <SelectValue placeholder="Selectează..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Nu</SelectItem>
                    <SelectItem value="1">Da</SelectItem>
                    <SelectItem value="9">N/A</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cpap36">CPAP nazal la 36 săptămâni</Label>
                <Select value={cpap36} onValueChange={setCpap36} disabled={readOnly}>
                  <SelectTrigger id="cpap36">
                    <SelectValue placeholder="Selectează..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Nu</SelectItem>
                    <SelectItem value="1">Da</SelectItem>
                    <SelectItem value="9">N/A</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Steroids for BPD */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Steroizi pentru BPD/CLD</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="steroidsCld"
                  checked={steroidsCld}
                  onCheckedChange={(checked) => setSteroidsCld(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="steroidsCld" className="text-sm font-normal cursor-pointer">
                  Steroizi pentru BPD/CLD
                </Label>
              </div>

              {steroidsCld && (
                <div className="space-y-2">
                  <Label htmlFor="steroidsCldWhere">Unde au fost administrați</Label>
                  <Select value={steroidsCldWhere} onValueChange={setSteroidsCldWhere} disabled={readOnly}>
                    <SelectTrigger id="steroidsCldWhere">
                      <SelectValue placeholder="Selectează..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Spitalul dumneavoastră</SelectItem>
                      <SelectItem value="2">Alt spital</SelectItem>
                      <SelectItem value="3">Ambele</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {!readOnly && (
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvează
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};
